import {
  RefreshCw,
  CalendarDays,
  MoreVertical,
  Download,
  Share2,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TruncatedText } from "@/components/ui/truncated-text";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SingleDatePicker } from "@/components/shared/SingleDatePicker";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { TabHeader } from "@/components/shared/TabHeader";
import { toast as sonnerToast } from "sonner";
import type { SPIResult, CosmoQuestion, ScorecardListItem } from "@/types/scorecard";
import { COSMO_CATEGORY_ORDER, COSMO_CATEGORY_LABELS } from "@/types/scorecard";
import { format, parseISO } from "date-fns";
import { DeltaBadge } from "./SPIScorecard/DeltaBadge";
import { QuestionRow } from "./SPIScorecard/QuestionRow";
import { StrategicRecommendations } from "./SPIScorecard/StrategicRecommendations";
import { statusConfig } from "./SPIScorecard/constants";
import { exportScorecardPdf } from "./SPIScorecard/utils";

interface SPIScorecardProps {
  result: SPIResult;
  onBack?: () => void;
  hideBackButton?: boolean;
  hideProductInfo?: boolean;
  isPublic?: boolean;
  asin?: string;
  brandId?: number;
  onSelectScorecard?: (id: number) => void;
  selectedScorecardId?: number;
  scorecardList?: ScorecardListItem[];
  /**
   * AUT-366: label for the NEWEST entry in `scorecardList`, replacing its date. The standalone
   * screen passes `"Current"`, which is what the design shows there; the product tab and the
   * share view leave it unset and keep dates on every row. Anchored to the newest rather than
   * the selected one, so picking an older report does not rename it.
   */
  newestOptionLabel?: string;
  readOnly?: boolean;
  onRerun?: () => void;
  rerunning?: boolean;
  onCreateAbTest?: (question: CosmoQuestion) => void;
  /**
   * `"full"` (default) is every other mount: the standalone screen, the public share view and the
   * A/B-test drawer. `"product-tab"` is the product detail audit tab, and is the one value that
   * drops the grade block, `StrategicRecommendations`, the Gaps/All Questions split and the
   * picker's score suffix — see `decisions/the-audit-tab-is-a-variant-of-one-scorecard-component.md`.
   */
  variant?: "full" | "product-tab";
}

export function SPIScorecard({ result, hideProductInfo, isPublic, asin, onSelectScorecard, selectedScorecardId, scorecardList, newestOptionLabel, readOnly = false, onRerun, rerunning, onCreateAbTest, variant = "full" }: SPIScorecardProps) {
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedScorecardId !== undefined) {
      setSelectedReport(String(selectedScorecardId));
    } else if (scorecardList && scorecardList.length > 0 && selectedReport === "") {
      setSelectedReport(String(scorecardList[0].id));
    }
  }, [selectedScorecardId, scorecardList, selectedReport]);

  const handleReportChange = (value: string) => {
    setSelectedReport(value);
    onSelectScorecard?.(Number(value));
  };

  const handleExport = async () => {
    if (!reportRef.current) {
      sonnerToast.error("Amazon AI Score not ready, try again");
      return;
    }
    try {
      flushSync(() => { setIsExporting(true); });
      const targetAsin = asin ?? result.asin;
      await exportScorecardPdf(reportRef.current, targetAsin);
    } catch (error) {
      sonnerToast.error(`Export failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsExporting(false);
    }
  };

  const reportOptions = (scorecardList ?? []).map((sc, i) => ({
    id: String(sc.id),
    label:
      newestOptionLabel && i === 0
        ? newestOptionLabel
        : `${format(parseISO(sc.createdAt), "MMMM d, yyyy")}${variant === "full" && sc.overallGrade ? ` · ${sc.overallGrade}${sc.overallScore != null ? ` (${sc.overallScore})` : ""}` : ""}`,
  }));

  // AUT-437: the product tab's header actions. The header sits OUTSIDE `reportRef`, which is the only
  // element the PDF export captures, so these controls never reach the PDF and need no
  // `data-html2canvas-ignore` (the `full` variant's toolbar lives inside the report and keeps it).
  const productTabMenuItems = (
    <>
      <DropdownMenuItem onClick={handleExport} disabled={isExporting}>
        <Download className="h-4 w-4 mr-2" />
        {isExporting ? "Exporting..." : "Export"}
      </DropdownMenuItem>
      {!readOnly ? (
        <DropdownMenuItem onClick={() => setShareOpen(true)}>
          <Share2 className="h-4 w-4 mr-2" /> Share
        </DropdownMenuItem>
      ) : null}
    </>
  );

  const productTabHeader = (
    <TabHeader title="Amazon AI Score" menuItems={!isPublic ? productTabMenuItems : undefined}>
      {!isPublic ? (
        <>
          <SingleDatePicker
            options={reportOptions}
            value={selectedReport}
            onValueChange={handleReportChange}
            placeholder="Select report"
          />
          {onRerun && (
            <Button onClick={onRerun} disabled={rerunning} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${rerunning ? "animate-spin" : ""}`} />
              {rerunning ? "Refreshing..." : "Refresh"}
            </Button>
          )}
        </>
      ) : null}
    </TabHeader>
  );

  const toolbar = (
    <div data-html2canvas-ignore="true" className="flex items-center justify-end gap-2">
      <Select value={selectedReport} onValueChange={handleReportChange}>
        <SelectTrigger className="w-[170px] text-xs bg-card" style={{ borderRadius: "8px" }}>
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <SelectValue placeholder="Select report" />
        </SelectTrigger>
        <SelectContent className="bg-popover" style={{ borderRadius: "8px" }}>
          {reportOptions.map((o) => (
            <SelectItem key={o.id} value={o.id} className="text-xs">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {onRerun && (
        <Button variant="outline" onClick={onRerun} disabled={rerunning} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${rerunning ? "animate-spin" : ""}`} />
          {rerunning ? "Refreshing..." : "Refresh"}
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="w-8">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-popover">
          <DropdownMenuItem onClick={handleExport} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export PDF"}
          </DropdownMenuItem>
          {!readOnly ? (
            <DropdownMenuItem onClick={() => setShareOpen(true)}>
              <Share2 className="h-4 w-4 mr-2" /> Share
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className={`space-y-6 ${hideProductInfo ? "" : "max-w-4xl mx-auto"}`}>
      {variant === "product-tab" && productTabHeader}
      <div ref={reportRef} className="space-y-6" data-testid="scorecard-report">
      {!isPublic && variant === "full" && toolbar}

      <Card>
        <CardContent className="p-6">
          {!hideProductInfo && (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Product</p>
              <p className="text-sm font-normal text-foreground leading-relaxed">{result.productTitle}</p>
              <p className="text-xs text-muted-foreground mt-1">{result.asin}</p>
            </div>
          )}

          <div className="flex items-center gap-8">
            {variant === "full" && (
              <>
                <div className="flex flex-col shrink-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Grade</p>
                  <div className="flex items-center gap-3">
                    <span className="text-6xl font-semibold leading-none text-foreground">{result.overallGrade}</span>
                    <div className="flex flex-col leading-none">
                      <span className="text-lg font-semibold text-foreground">
                        {result.overallScore}
                        <DeltaBadge current={result.overallScore} previous={result.previousOverallScore} />
                      </span>
                      <span className="text-[10px] text-muted-foreground mt-0.5">/ 100</span>
                    </div>
                  </div>
                </div>

                <div className="h-20 w-px bg-border shrink-0" />
              </>
            )}

            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">COSMO Categories</p>
              <div className="grid grid-cols-5 gap-4">
                {COSMO_CATEGORY_ORDER.map((catKey) => {
                  const cat = result.categoryBreakdown.find((c) => c.category === catKey);
                  if (!cat) return (
                    <div key={catKey} className="min-w-0">
                      <p className="text-sm text-foreground mb-1.5">
                        <TruncatedText>{COSMO_CATEGORY_LABELS[catKey]}</TruncatedText>
                      </p>
                      <div className="w-full bg-secondary rounded-full h-2 mb-2" />
                      <p className="text-lg font-semibold text-muted-foreground">--</p>
                    </div>
                  );
                  const label = COSMO_CATEGORY_LABELS[cat.category] ?? cat.category;
                  if (cat.score === null) {
                    return (
                      <div key={cat.category} className="min-w-0">
                        <p className="text-sm text-foreground mb-1.5">
                          <TruncatedText>{label}</TruncatedText>
                        </p>
                        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary mb-1.5" />
                        <p className="text-lg font-semibold text-slate-400">N/A</p>
                      </div>
                    );
                  }
                  const barColor = cat.score >= 80 ? "bg-emerald-500" : cat.score >= 60 ? "bg-amber-500" : "bg-red-500";
                  const scoreColor = cat.score >= 80 ? "text-emerald-600" : cat.score >= 60 ? "text-amber-600" : "text-red-600";
                  return (
                    <div key={cat.category} className="min-w-0">
                      <p className="text-sm text-foreground mb-1.5">
                        <TruncatedText>{label}</TruncatedText>
                      </p>
                      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary mb-1.5">
                        <div
                          className={`h-full rounded-full transition-all ${barColor}`}
                          style={{ width: `${cat.score}%` }}
                        />
                      </div>
                      <p className={`text-lg font-semibold ${scoreColor}`}>
                        {cat.score}
                        <DeltaBadge current={cat.score} previous={cat.previousScore} />
                        <span className="text-[10px] font-normal text-muted-foreground ml-0.5">/ 100</span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </CardContent>
      </Card>

      {variant === "full" && (
        <StrategicRecommendations questions={result.questions} onCreateAbTest={onCreateAbTest} />
      )}

      {variant === "full" ? (
        <div className="space-y-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground">Detailed Analysis</h3>
            <p className="text-sm text-muted-foreground mt-0.5">From the {result.questions.length} COSMO questions</p>
          </div>
          <Tabs defaultValue="gaps" className="space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <TabsList className="bg-muted/50">
                <TabsTrigger value="gaps">
                  Gaps
                  <span className="ml-2 text-xs opacity-70">
                    ({result.questions.filter((q) => q.status !== "strong" && q.status !== "not_applicable").length})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="all">
                  All Questions
                  <span className="ml-2 text-xs opacity-70">({result.questions.length})</span>
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1"><statusConfig.strong.icon className={`h-3.5 w-3.5 ${statusConfig.strong.color}`} /> {result.questions.filter((q) => q.status === "strong").length} {statusConfig.strong.label}</span>
                <span className="flex items-center gap-1"><statusConfig.moderate.icon className={`h-3.5 w-3.5 ${statusConfig.moderate.color}`} /> {result.questions.filter((q) => q.status === "moderate").length} {statusConfig.moderate.label}</span>
                <span className="flex items-center gap-1"><statusConfig.weak.icon className={`h-3.5 w-3.5 ${statusConfig.weak.color}`} /> {result.questions.filter((q) => q.status === "weak" || q.status === "missing").length} {statusConfig.weak.label}</span>
                <span className="flex items-center gap-1"><statusConfig.not_applicable.icon className={`h-3.5 w-3.5 ${statusConfig.not_applicable.color}`} /> {result.questions.filter((q) => q.status === "not_applicable").length} {statusConfig.not_applicable.label}</span>
              </div>
            </div>

            {(["all", "gaps"] as const).map((tab) => (
              <TabsContent key={tab} value={tab} className="space-y-6 mt-0">
                {COSMO_CATEGORY_ORDER.map((cat) => {
                  const catQuestions = result.questions.filter((q) => {
                    if (q.category !== cat) return false;
                    if (tab === "gaps") return q.status !== "strong" && q.status !== "not_applicable";
                    return true;
                  });
                  if (catQuestions.length === 0) return null;
                  return (
                    <div key={cat}>
                      <div className="px-3 py-2.5 border-b border-border">
                        <span className="font-semibold text-sm text-foreground">{COSMO_CATEGORY_LABELS[cat]}</span>
                      </div>
                      <div className="divide-y divide-border/50">
                        {catQuestions.map((q) => (
                          <QuestionRow
                            key={q.id}
                            question={q}
                            forceExpand={isExporting}
                            onCreateAbTest={!isPublic ? onCreateAbTest : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
                {tab === "gaps" && result.questions.every((q) => q.status === "strong" || q.status === "not_applicable") && (
                  <div className="text-center text-sm text-muted-foreground py-12 border border-dashed border-border rounded-lg">
                    {result.questions.some((q) => q.status === "strong")
                      ? `No gaps — every COSMO question that applies scored ${statusConfig.strong.label}.`
                      : "No gaps — no COSMO question applied to this listing, so nothing was scored."}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      ) : (
        <div className="space-y-6">
          {COSMO_CATEGORY_ORDER.map((cat) => {
            const catQuestions = result.questions.filter((q) => q.category === cat);
            if (catQuestions.length === 0) return null;
            return (
              <div key={cat}>
                <div className="px-3 py-2.5 border-b border-border">
                  <span className="font-semibold text-sm text-foreground">{COSMO_CATEGORY_LABELS[cat]}</span>
                </div>
                <div className="divide-y divide-border/50">
                  {catQuestions.map((q) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      forceExpand={isExporting}
                      onCreateAbTest={!isPublic ? onCreateAbTest : undefined}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {!isPublic && !readOnly && (
        <ShareLinkModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          shareableType="scorecard"
          shareableId={selectedReport || String(selectedScorecardId ?? "")}
          resourceName={result.productTitle}
        />
      )}
    </div>
  );
}
